// skills/woodworking/woodworking.js
// Woodworking skill logic

import { SHAFT_RECIPES, ARROW_RECIPES, BOW_RECIPES, STAFF_RECIPES, TOOLS } from './woodworkingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { bountySkill } from '../bounty/bounty.js';
import {
    getTalentFlatBonus,
    rollTalentProc,
    incrementStackCount,
    resetStackCount
} from '../../systems/talentTree/talentTree.js';
import { GLYPH_RECIPES } from '../glyphbinding/glyphbindingData.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

/**
 * Get effective log quantity needed after Prime Timber talent reduction
 * Prime Timber: "1 log = 1.25 logs" → effectively need fewer logs
 * @param {number} required - Base quantity required
 * @param {string} itemId - The input item ID
 * @returns {number} Effective quantity needed
 */
function getEffectiveLogQuantity(required, itemId) {
    if (!itemId || !itemId.endsWith('_logs')) return required;
    const primeTimber = getTalentFlatBonus(SKILLS.WOODCUTTING, 'primeTimber');
    if (primeTimber <= 0) return required;
    return Math.max(1, Math.ceil(required / (1 + primeTimber)));
}

class WoodworkingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.WOODWORKING, TOOLS, 'PYRESTONE');
        // Track instant craft proc (Perfect Form talent)
        this.nextCraftInstant = false;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Get effective woodworking time based on tool
     */
    getWoodworkingTime(baseTime) {
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        return baseTime / speedMultiplier;
    }

    // ============================================
    // Shaft Methods
    // ============================================

    canMakeShafts(shaftId) {
        const recipe = Object.values(SHAFT_RECIPES).find(r => r.id === shaftId);
        if (!recipe) return { success: false, reason: 'Recipe not found' };

        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        if (skill.level < recipe.level) {
            return { success: false, reason: `Requires level ${recipe.level} Woodworking` };
        }

        const effectiveQty = getEffectiveLogQuantity(recipe.inputQuantity, recipe.inputItem);
        const hasInput = gameState.getItemCount(recipe.inputItem) >= effectiveQty;
        if (!hasInput) {
            return { success: false, reason: `Requires ${effectiveQty}x ${recipe.inputItem.replace(/_/g, ' ')}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startMakingShafts(shaftId, showToast = true) {
        const recipe = Object.values(SHAFT_RECIPES).find(r => r.id === shaftId);
        const canMake = this.canMakeShafts(shaftId);

        if (!canMake.success) {
            if (showToast) toastWarning(canMake.reason);
            return false;
        }

        // Check for instant craft (Perfect Form talent)
        if (this.nextCraftInstant) {
            this.nextCraftInstant = false;
            this.completeMakingShafts(recipe);
            // Check if we can continue
            const canContinue = this.canMakeShafts(shaftId);
            if (canContinue.success) {
                this.startMakingShafts(shaftId, false);
            } else {
                gameState.clearAction();
            }
            return true;
        }

        const woodworkingTime = this.getWoodworkingTime(recipe.baseTime);

        const action = {
            type: 'woodworking',
            subType: 'shafts',
            name: `Making ${recipe.name}`,
            recipe: recipe,
            recipeId: shaftId,
            progress: 0,
            duration: woodworkingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeMakingShafts(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeShafts(shaftId);
                    if (canContinue.success) {
                        this.startMakingShafts(shaftId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeMakingShafts(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeShafts(shaftId);
                    if (canContinue.success) {
                        this.startMakingShafts(shaftId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeMakingShafts(recipe) {
        // Roll for log save proc (Wood Whisperer talent)
        const logSaved = rollTalentProc(SKILLS.WOODWORKING, 'logSave');
        if (!logSaved) {
            // Consume input materials only if not saved (with Prime Timber reduction)
            const effectiveQty = getEffectiveLogQuantity(recipe.inputQuantity, recipe.inputItem);
            const removed = gameState.removeItem(recipe.inputItem, effectiveQty);
            if (!removed) {
                logger.warn('Failed to remove materials for shafts');
                this.stopMakingShafts();
                return;
            }
        } else {
            recordTalentProc(SKILLS.WOODWORKING, 'Wood Whisperer', 'Logs saved!', null);
        }

        const xpResult = addXP(SKILLS.WOODWORKING, recipe.xp, recipe.id);

        // Calculate bonus shafts from Bulk Production talent
        const shaftBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'shaftBonus');
        let drops = recipe.drops;
        if (shaftBonus > 0) {
            // Clone and modify drops with bonus quantity
            drops = recipe.drops.map(drop => ({
                ...drop,
                quantity: Math.floor(drop.quantity * (1 + shaftBonus))
            }));
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.WOODWORKING, drops);

        // Roll for corruption proc
        rollForCorruption(SKILLS.WOODWORKING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('woodworking');

        // Track consecutive actions for Assembly Line stacking bonus
        incrementStackCount(SKILLS.WOODWORKING, recipe.id);

        this.onShaftsComplete(recipe, xpResult);

        // Check for instant craft proc (Perfect Form talent) - applies to next action
        if (rollTalentProc(SKILLS.WOODWORKING, 'instantCraft')) {
            this.nextCraftInstant = true;
            recordTalentProc(SKILLS.WOODWORKING, 'Perfect Form', 'Instant next craft!', null);
        }
    }

    stopMakingShafts() {
        resetStackCount(SKILLS.WOODWORKING);
        this.nextCraftInstant = false;
        gameState.clearAction();
    }

    getAvailableShaftRecipes() {
        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        return Object.values(SHAFT_RECIPES).filter(r => r.level <= skill.level);
    }

    onShaftsComplete(recipe, xpResult) {
        logger.game(`Made ${recipe.name}! +${recipe.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Woodworking level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Arrow Methods
    // ============================================

    canMakeArrows(arrowId) {
        const recipe = Object.values(ARROW_RECIPES).find(r => r.id === arrowId);
        if (!recipe) return { success: false, reason: 'Recipe not found' };

        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        if (skill.level < recipe.level) {
            return { success: false, reason: `Requires level ${recipe.level} Woodworking` };
        }

        for (const input of recipe.inputItems) {
            const hasInput = gameState.getItemCount(input.itemId) >= input.quantity;
            if (!hasInput) {
                return { success: false, reason: `Requires ${input.quantity}x ${input.itemId.replace(/_/g, ' ')}` };
            }
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startMakingArrows(arrowId, showToast = true) {
        const recipe = Object.values(ARROW_RECIPES).find(r => r.id === arrowId);
        const canMake = this.canMakeArrows(arrowId);

        if (!canMake.success) {
            if (showToast) toastWarning(canMake.reason);
            return false;
        }

        // Check for instant craft (Perfect Form talent)
        if (this.nextCraftInstant) {
            this.nextCraftInstant = false;
            this.completeMakingArrows(recipe);
            // Check if we can continue
            const canContinue = this.canMakeArrows(arrowId);
            if (canContinue.success) {
                this.startMakingArrows(arrowId, false);
            } else {
                gameState.clearAction();
            }
            return true;
        }

        const woodworkingTime = this.getWoodworkingTime(recipe.baseTime);

        const action = {
            type: 'woodworking',
            subType: 'arrows',
            name: `Making ${recipe.name}`,
            recipe: recipe,
            recipeId: arrowId,
            progress: 0,
            duration: woodworkingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeMakingArrows(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeArrows(arrowId);
                    if (canContinue.success) {
                        this.startMakingArrows(arrowId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeMakingArrows(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeArrows(arrowId);
                    if (canContinue.success) {
                        this.startMakingArrows(arrowId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeMakingArrows(recipe) {
        // Consume input materials - check each removal
        for (const input of recipe.inputItems) {
            const removed = gameState.removeItem(input.itemId, input.quantity);
            if (!removed) {
                logger.warn(`Failed to remove ${input.itemId} for arrows`);
                this.stopMakingArrows();
                return;
            }
        }

        const xpResult = addXP(SKILLS.WOODWORKING, recipe.xp, recipe.id);

        // Start with base drops
        let drops = recipe.drops.map(d => ({ ...d }));
        const outputItemId = recipe.drops[0]?.itemId;
        let arrowStormProc = false;
        let bonusArrowsProc = false;

        // Check for Arrow Storm proc (3x arrows) - highest priority
        if (rollTalentProc(SKILLS.WOODWORKING, 'arrowStorm')) {
            drops = drops.map(drop => ({
                ...drop,
                quantity: drop.quantity * 3
            }));
            arrowStormProc = true;
        }
        // Check for bonus arrows proc (Precise Fletching talent)
        else if (rollTalentProc(SKILLS.WOODWORKING, 'bonusArrows')) {
            drops = drops.map(drop => ({
                ...drop,
                quantity: drop.quantity + 5
            }));
            bonusArrowsProc = true;
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.WOODWORKING, drops);

        // Record proc notifications after drops awarded
        if (arrowStormProc && outputItemId) {
            recordTalentProc(SKILLS.WOODWORKING, 'Arrow Storm', null, null, { itemId: outputItemId, quantity: drops[0].quantity });
        } else if (bonusArrowsProc && outputItemId) {
            recordTalentProc(SKILLS.WOODWORKING, 'Precise Fletching', null, null, { itemId: outputItemId, quantity: 5 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.WOODWORKING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('woodworking');

        // Track consecutive actions for Assembly Line stacking bonus
        incrementStackCount(SKILLS.WOODWORKING, recipe.id);

        this.onArrowsComplete(recipe, xpResult);

        // Check for instant craft proc (Perfect Form talent) - applies to next action
        if (rollTalentProc(SKILLS.WOODWORKING, 'instantCraft')) {
            this.nextCraftInstant = true;
            recordTalentProc(SKILLS.WOODWORKING, 'Perfect Form', 'Instant next craft!', null);
        }
    }

    stopMakingArrows() {
        resetStackCount(SKILLS.WOODWORKING);
        this.nextCraftInstant = false;
        gameState.clearAction();
    }

    getAvailableArrowRecipes() {
        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        return Object.values(ARROW_RECIPES).filter(r => r.level <= skill.level);
    }

    onArrowsComplete(recipe, xpResult) {
        logger.game(`Made ${recipe.name}! +${recipe.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Woodworking level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Bow Methods
    // ============================================

    canMakeBow(bowId) {
        const recipe = Object.values(BOW_RECIPES).find(r => r.id === bowId);
        if (!recipe) return { success: false, reason: 'Recipe not found' };

        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        if (skill.level < recipe.level) {
            return { success: false, reason: `Requires level ${recipe.level} Woodworking` };
        }

        const effectiveBowQty = getEffectiveLogQuantity(recipe.inputQuantity, recipe.inputItem);
        const hasInput = gameState.getItemCount(recipe.inputItem) >= effectiveBowQty;
        if (!hasInput) {
            return { success: false, reason: `Requires ${effectiveBowQty}x ${recipe.inputItem.replace(/_/g, ' ')}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startMakingBow(bowId, showToast = true) {
        const recipe = Object.values(BOW_RECIPES).find(r => r.id === bowId);
        const canMake = this.canMakeBow(bowId);

        if (!canMake.success) {
            if (showToast) toastWarning(canMake.reason);
            return false;
        }

        // Check for instant craft (Perfect Form talent)
        if (this.nextCraftInstant) {
            this.nextCraftInstant = false;
            this.completeMakingBow(recipe);
            // Check if we can continue
            const canContinue = this.canMakeBow(bowId);
            if (canContinue.success) {
                this.startMakingBow(bowId, false);
            } else {
                gameState.clearAction();
            }
            return true;
        }

        const woodworkingTime = this.getWoodworkingTime(recipe.baseTime);

        const action = {
            type: 'woodworking',
            subType: 'bows',
            name: `Making ${recipe.name}`,
            recipe: recipe,
            recipeId: bowId,
            progress: 0,
            duration: woodworkingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeMakingBow(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeBow(bowId);
                    if (canContinue.success) {
                        this.startMakingBow(bowId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeMakingBow(recipe);
                    // Check if we can continue (no toast on auto-restart)
                    const canContinue = this.canMakeBow(bowId);
                    if (canContinue.success) {
                        this.startMakingBow(bowId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeMakingBow(recipe) {
        // Roll for log save proc (Wood Whisperer talent)
        const logSaved = rollTalentProc(SKILLS.WOODWORKING, 'logSave');
        if (!logSaved) {
            // Consume input materials only if not saved (with Prime Timber reduction)
            const effectiveBowQty = getEffectiveLogQuantity(recipe.inputQuantity, recipe.inputItem);
            const removed = gameState.removeItem(recipe.inputItem, effectiveBowQty);
            if (!removed) {
                logger.warn('Failed to remove materials for bow');
                this.stopMakingBow();
                return;
            }
        } else {
            recordTalentProc(SKILLS.WOODWORKING, 'Wood Whisperer', 'Logs saved!', null);
        }

        const xpResult = addXP(SKILLS.WOODWORKING, recipe.xp, recipe.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.WOODWORKING, recipe.drops);

        // Roll for corruption proc
        rollForCorruption(SKILLS.WOODWORKING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('woodworking');

        // Track consecutive actions for Assembly Line stacking bonus
        incrementStackCount(SKILLS.WOODWORKING, recipe.id);

        this.onBowComplete(recipe, xpResult);

        // Check for instant craft proc (Perfect Form talent) - applies to next action
        if (rollTalentProc(SKILLS.WOODWORKING, 'instantCraft')) {
            this.nextCraftInstant = true;
            recordTalentProc(SKILLS.WOODWORKING, 'Perfect Form', 'Instant next craft!', null);
        }
    }

    stopMakingBow() {
        resetStackCount(SKILLS.WOODWORKING);
        this.nextCraftInstant = false;
        gameState.clearAction();
    }

    getAvailableBowRecipes() {
        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        return Object.values(BOW_RECIPES).filter(r => r.level <= skill.level);
    }

    onBowComplete(recipe, xpResult) {
        logger.game(`Made ${recipe.name}! +${recipe.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Woodworking level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Staff Methods
    // ============================================

    canMakeStaff(staffId) {
        const recipe = Object.values(STAFF_RECIPES).find(r => r.id === staffId);
        if (!recipe) return { success: false, reason: 'Recipe not found' };

        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        if (skill.level < recipe.level) {
            return { success: false, reason: `Requires level ${recipe.level} Woodworking` };
        }

        // Check all input materials (with Prime Timber reduction for logs)
        const missingMaterials = [];
        for (const input of recipe.inputItems) {
            const effectiveQty = getEffectiveLogQuantity(input.quantity, input.itemId);
            const playerQty = gameState.getItemCount(input.itemId);
            if (playerQty < effectiveQty) {
                const itemName = input.itemId.replace(/_/g, ' ');
                missingMaterials.push(`${itemName}: ${playerQty}/${effectiveQty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return { success: false, reason: `Missing: ${missingMaterials.join(', ')}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startMakingStaff(staffId, showToast = true) {
        const recipe = Object.values(STAFF_RECIPES).find(r => r.id === staffId);
        const canMake = this.canMakeStaff(staffId);

        if (!canMake.success) {
            if (showToast) toastWarning(canMake.reason);
            return false;
        }

        // Check for instant craft (Perfect Form talent)
        if (this.nextCraftInstant) {
            this.nextCraftInstant = false;
            this.completeMakingStaff(recipe);
            // Check if we can continue
            const canContinue = this.canMakeStaff(staffId);
            if (canContinue.success) {
                this.startMakingStaff(staffId, false);
            } else {
                gameState.clearAction();
            }
            return true;
        }

        const woodworkingTime = this.getWoodworkingTime(recipe.baseTime);

        const action = {
            type: 'woodworking',
            subType: 'staffs',
            name: `Making ${recipe.name}`,
            recipe: recipe,
            recipeId: staffId,
            progress: 0,
            duration: woodworkingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeMakingStaff(recipe);
                    const canContinue = this.canMakeStaff(staffId);
                    if (canContinue.success) {
                        this.startMakingStaff(staffId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeMakingStaff(recipe);
                    const canContinue = this.canMakeStaff(staffId);
                    if (canContinue.success) {
                        this.startMakingStaff(staffId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeMakingStaff(recipe) {
        // Roll for log save proc (Wood Whisperer talent) - only applies to logs, not glyph cores
        const logSaved = rollTalentProc(SKILLS.WOODWORKING, 'logSave');

        // Consume all input materials (with Prime Timber reduction for logs)
        for (let i = 0; i < recipe.inputItems.length; i++) {
            const input = recipe.inputItems[i];
            // Log is always first input - skip if saved
            if (i === 0 && logSaved) {
                continue;
            }
            const effectiveQty = getEffectiveLogQuantity(input.quantity, input.itemId);
            const removed = gameState.removeItem(input.itemId, effectiveQty);
            if (!removed) {
                logger.warn(`Failed to remove ${input.itemId} for staff`);
                this.stopMakingStaff();
                return;
            }
        }

        if (logSaved) {
            recordTalentProc(SKILLS.WOODWORKING, 'Wood Whisperer', 'Logs saved!', null);
        }

        // Calculate XP with Staff Artisan bonus (staffXpBonus from Arcane Sensitivity)
        let effectiveXP = recipe.xp;
        const staffXpBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'staffXpBonus');
        if (staffXpBonus > 0) {
            effectiveXP = Math.floor(effectiveXP * (1 + staffXpBonus));
        }

        const xpResult = addXP(SKILLS.WOODWORKING, effectiveXP, recipe.id);

        // Award Glyphbinding XP from Glyph Resonance talent
        const glyphXpBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'glyphbindingXpOnStaff');
        if (glyphXpBonus > 0) {
            const glyphXP = Math.floor(recipe.xp * glyphXpBonus);
            if (glyphXP > 0) {
                addXP(SKILLS.GLYPHBINDING, glyphXP, recipe.id, { skipCorruption: true });
            }
        }

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.WOODWORKING, recipe.drops);

        // Check for bonus ichor proc (Arcane Overflow talent) - random ichor type
        if (rollTalentProc(SKILLS.WOODWORKING, 'bonusIchor')) {
            this.awardRandomIchor();
        }

        // Check for Veil-Touched Timber proc (random glyph based on Glyphbinding level)
        if (rollTalentProc(SKILLS.WOODWORKING, 'veilTimber')) {
            this.awardRandomGlyph();
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.WOODWORKING);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('woodworking');

        // Track consecutive actions for Assembly Line stacking bonus
        incrementStackCount(SKILLS.WOODWORKING, recipe.id);

        this.onStaffComplete(recipe, xpResult);

        // Check for instant craft proc (Perfect Form talent) - applies to next action
        if (rollTalentProc(SKILLS.WOODWORKING, 'instantCraft')) {
            this.nextCraftInstant = true;
            recordTalentProc(SKILLS.WOODWORKING, 'Perfect Form', 'Instant next craft!', null);
        }
    }

    stopMakingStaff() {
        resetStackCount(SKILLS.WOODWORKING);
        this.nextCraftInstant = false;
        gameState.clearAction();
    }

    getAvailableStaffRecipes() {
        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        return Object.values(STAFF_RECIPES).filter(r => r.level <= skill.level);
    }

    onStaffComplete(recipe, xpResult) {
        logger.game(`Made ${recipe.name}! +${recipe.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Woodworking level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Helper Methods for Talent Procs
    // ============================================

    /**
     * Award a random ichor type (Arcane Overflow talent)
     * Equal chance for any ichor type
     */
    awardRandomIchor() {
        const ichorTypes = ['arcane_residue', 'refined_arcane_ichor', 'purified_arcane_core'];
        const randomIchor = ichorTypes[Math.floor(Math.random() * ichorTypes.length)];
        gameState.addItem(randomIchor, 1);
        bountySkill.trackItemProgress(randomIchor, 1);
        recordTalentProc(SKILLS.WOODWORKING, 'Arcane Overflow', null, null, { itemId: randomIchor, quantity: 1 });
    }

    /**
     * Award a random glyph based on Glyphbinding level (Veil-Touched Timber talent)
     * Only awards glyphs the player can craft based on their level
     */
    awardRandomGlyph() {
        const glyphbindingLevel = gameState.getSkill(SKILLS.GLYPHBINDING).level;

        // Filter glyphs by level
        const availableGlyphs = Object.values(GLYPH_RECIPES)
            .filter(g => g.level <= glyphbindingLevel)
            .map(g => g.drops[0].itemId);

        if (availableGlyphs.length === 0) {
            // Fallback to storm_glyph if no glyphs available (shouldn't happen)
            gameState.addItem('storm_glyph', 1);
            bountySkill.trackItemProgress('storm_glyph', 1);
            recordTalentProc(SKILLS.WOODWORKING, 'Veil-Touched Timber', null, null, { itemId: 'storm_glyph', quantity: 1 });
            return;
        }

        const randomGlyph = availableGlyphs[Math.floor(Math.random() * availableGlyphs.length)];
        gameState.addItem(randomGlyph, 1);
        bountySkill.trackItemProgress(randomGlyph, 1);
        recordTalentProc(SKILLS.WOODWORKING, 'Veil-Touched Timber', null, null, { itemId: randomGlyph, quantity: 1 });
    }

    // ============================================
    // Tool Methods
    // ============================================

    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.WOODWORKING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Woodworking` };
        }

        if (gameState.hasPurchasedTool(SKILLS.WOODWORKING, toolId)) {
            gameState.equipTool(SKILLS.WOODWORKING, toolId);
            return { success: true, action: 'equipped' };
        }

        const purchaseResult = gameState.purchaseTool(SKILLS.WOODWORKING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        gameState.equipTool(SKILLS.WOODWORKING, toolId);
        return { success: true, action: 'purchased' };
    }

    // ============================================
    // Unified Stop Method
    // ============================================

    stopWoodworking() {
        resetStackCount(SKILLS.WOODWORKING);
        this.nextCraftInstant = false;
        gameState.clearAction();
    }
}

export const woodworkingSkill = new WoodworkingSkill();
export default woodworkingSkill;
